import React, { useEffect, useMemo, useState } from 'react';
import {
  Search, User, Users, LayoutDashboard, AlertTriangle, Clock,
  CheckCircle2, Inbox, ChevronRight, Zap, Timer, Flame,
  LayoutGrid, List,
} from 'lucide-react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { stageMeta, waitingOnMeta } from './MirWorkflowStepper';
import { useAuth } from '../../contexts/AuthContext';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Helpers ────────────────────────────────────────────────────────
const daysUntil = (iso) => {
  if (!iso) return null;
  const target = new Date(iso + (String(iso).length === 10 ? 'T23:59:59' : ''));
  return Math.floor((target.getTime() - Date.now()) / 86400000);
};
const daysSince = (iso) => {
  if (!iso) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(String(iso).length === 10 ? `${iso}T00:00:00` : iso)
      .toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  } catch { return String(iso); }
};
const initials = (name) => (name || '?').split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();

/**
 * Turn every pack into 0..N tasks.
 * Emits per-MIR tasks + a pack-level task for pre-lodgement files that
 * currently need someone's attention.
 */
function packsToTasks(packs, currentUserId) {
  const tasks = [];
  packs.forEach((p) => {
    const isPost = p.phase === 'post_lodgement';
    const isPre = p.phase === 'pre_lodgement';
    (p.mirs || []).forEach((m) => {
      if (m.completed || m.status === 'sent_to_lender' || m.status === 'completed') return;
      const dueIn = daysUntil(m.due_date);
      const isOverdue = dueIn !== null && dueIn < 0;
      const isDueToday = dueIn !== null && dueIn === 0;
      const stage = m.stage || 'awaiting_docs';
      const sm = stageMeta(stage);
      const wm = waitingOnMeta(m.waiting_on, p.assigned_users || []);
      const priority = isOverdue ? 'P1'
        : isDueToday ? 'P1'
        : (m.urgency === 'urgent') ? 'P1'
        : (dueIn !== null && dueIn <= 2) ? 'P2'
        : 'P3';
      tasks.push({
        key: `${p.id}-${m.id}`,
        packId: p.id,
        source: 'mir',
        phase: isPost ? 'post_lodgement' : 'pre_lodgement',
        client: p.client_name,
        lender: p.lender,
        title: m.description,
        docsRequired: m.documents_required,
        stage,
        stageLabel: sm.label,
        stageBg: sm.softBg,
        stageFg: sm.softFg,
        waitingOn: m.waiting_on || null,
        waitingLabel: wm?.label || null,
        waitingBg: wm?.softBg || null,
        waitingFg: wm?.softFg || null,
        assigneeId: m.assignee_id || null,
        assigneeName: m.assignee_name || null,
        assigneeRole: m.assignee_role || null,
        dueDate: m.due_date || null,
        dueIn,
        ageDays: daysSince(m.date_received || m.created_at),
        priority,
        isOverdue,
        isDueToday,
        isMine: !!(currentUserId && m.assignee_id === currentUserId),
      });
    });
    if (isPre && ['in_progress', 'waiting_on_info', 'ready_for_review', 'ready_to_submit', 'changes_required'].includes(p.status)) {
      const daysAgo = daysSince(p.last_updated_at || p.created_at);
      tasks.push({
        key: `${p.id}-pack`,
        packId: p.id,
        source: 'pack',
        phase: 'pre_lodgement',
        client: p.client_name,
        lender: p.lender,
        title: p.status === 'ready_for_review' ? `Ready for peer review — pack completion at ${p.completion || 0}%`
          : p.status === 'ready_to_submit' ? 'Ready to lodge with lender'
          : p.status === 'changes_required' ? 'Changes requested by credit team'
          : p.status === 'waiting_on_info' ? 'Waiting on info to complete pack'
          : `Pack in progress — ${p.completion || 0}% complete`,
        docsRequired: null,
        stage: p.status,
        stageLabel: p.status.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
        stageBg: '#e0e7ff',
        stageFg: '#3730a3',
        waitingOn: null,
        waitingLabel: null,
        assigneeId: p.credit_team_member_id || p.broker_id || null,
        assigneeName: p.credit_team_member || p.broker || null,
        assigneeRole: p.credit_team_member_id ? 'credit' : 'broker',
        dueDate: null,
        dueIn: null,
        ageDays: daysAgo,
        priority: daysAgo > 3 ? 'P1' : daysAgo > 1 ? 'P2' : 'P3',
        isOverdue: daysAgo > 5,
        isDueToday: false,
        isMine: !!(currentUserId && (p.credit_team_member_id === currentUserId || p.broker_id === currentUserId)),
      });
    }
  });
  return tasks;
}

function groupTasks(tasks) {
  const overdue = [], dueToday = [], upcoming = [], waiting = [];
  tasks.forEach((t) => {
    if (t.isOverdue) overdue.push(t);
    else if (t.isDueToday) dueToday.push(t);
    else if (t.waitingOn && !['csm', 'credit', 'broker'].includes(t.waitingOn) && !String(t.waitingOn).startsWith('user:')) {
      waiting.push(t);
    } else {
      upcoming.push(t);
    }
  });
  return { overdue, dueToday, upcoming, waiting };
}

/**
 * MyDashboardTab — embedded inside Lender Submission Packs.
 *
 * Props:
 *   onOpenPack(packId)  — open a pack in the same tool
 */
export default function MyDashboardTab({ onOpenPack }) {
  const { token, user } = useAuth();
  const currentUserId = user?.id;

  const auth = useMemo(() => {
    const h = { Authorization: `Bearer ${token}` };
    if (localStorage.getItem('mcc-demo-mode') === 'on') h['X-Demo-Mode'] = 'on';
    return h;
  }, [token]);

  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [scope, setScope] = useState('mine');
  const [assignee, setAssignee] = useState('all');
  const [role, setRole] = useState('all');
  const [phase, setPhase] = useState('all');
  const [priority, setPriority] = useState('all');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem('lp-mydash-view') || 'kanban');
  React.useEffect(() => { localStorage.setItem('lp-mydash-view', view); }, [view]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs`, { headers: auth });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setPacks(data.packs || []);
    } catch (e) {
      toast.error(`Failed to load dashboard — ${e.message}`);
    } finally { setLoading(false); }
  }, [auth]);
  useEffect(() => { load(); }, [load]);

  const allTasks = useMemo(() => packsToTasks(packs, currentUserId), [packs, currentUserId]);

  const distinctAssignees = useMemo(() => {
    const map = new Map();
    allTasks.forEach((t) => {
      if (t.assigneeId && t.assigneeName) map.set(t.assigneeId, { id: t.assigneeId, name: t.assigneeName, role: t.assigneeRole });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allTasks]);

  const filtered = useMemo(() => {
    let arr = allTasks;
    if (scope === 'mine' && currentUserId) arr = arr.filter((t) => t.isMine);
    if (assignee !== 'all') arr = arr.filter((t) => t.assigneeId === assignee);
    if (role !== 'all') arr = arr.filter((t) => t.assigneeRole === role);
    if (phase !== 'all') arr = arr.filter((t) => t.phase === phase);
    if (priority !== 'all') arr = arr.filter((t) => t.priority === priority);
    if (overdueOnly) arr = arr.filter((t) => t.isOverdue);
    if (q.trim()) {
      const query = q.trim().toLowerCase();
      arr = arr.filter((t) => `${t.client || ''} ${t.title || ''} ${t.assigneeName || ''} ${t.lender || ''}`.toLowerCase().includes(query));
    }
    arr.sort((a, b) => {
      if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
      if (a.priority !== b.priority) return a.priority < b.priority ? -1 : 1;
      const ad = a.dueIn ?? 999, bd = b.dueIn ?? 999;
      return ad - bd;
    });
    return arr;
  }, [allTasks, scope, currentUserId, assignee, role, phase, priority, overdueOnly, q]);

  const groups = useMemo(() => groupTasks(filtered), [filtered]);

  return (
    <div className="space-y-4">
      {/* Header + scope switch */}
      <div className="rounded-2xl border bg-white p-4 flex items-center gap-3 shadow-sm" data-testid="my-dashboard-header">
        <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center shrink-0">
          <LayoutDashboard className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-black tracking-tight text-slate-900">My Dashboard</h2>
          <p className="text-[11px] font-semibold text-slate-500">Every outstanding action across Pre-Lodgement and Post-Lodgement — one place, one owner per task.</p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden shrink-0">
          <button onClick={() => setScope('mine')} className={`px-3 py-1.5 text-[11px] font-black uppercase tracking-widest transition-colors ${scope === 'mine' ? 'bg-indigo-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`} data-testid="dash-scope-mine">
            <User className="w-3 h-3 inline mr-1" /> My Tasks
          </button>
          <button onClick={() => setScope('team')} className={`px-3 py-1.5 text-[11px] font-black uppercase tracking-widest transition-colors ${scope === 'team' ? 'bg-indigo-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`} data-testid="dash-scope-team">
            <Users className="w-3 h-3 inline mr-1" /> All Team
          </button>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden shrink-0">
          <button
            onClick={() => setView('kanban')}
            className={`px-2.5 py-1.5 transition-colors ${view === 'kanban' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-100'}`}
            data-testid="dash-view-kanban"
            title="Kanban board"
          >
            <LayoutGrid className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setView('list')}
            className={`px-2.5 py-1.5 transition-colors ${view === 'list' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-100'}`}
            data-testid="dash-view-list"
            title="List view"
          >
            <List className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border bg-white p-3 grid grid-cols-1 md:grid-cols-[1fr_170px_150px_170px_150px_120px] gap-2 shadow-sm">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, task, assignee…" className="h-9 pl-8 text-xs bg-white" data-testid="dash-search" />
        </div>
        <Select value={assignee} onValueChange={setAssignee}>
          <SelectTrigger className="h-9 text-xs bg-white" data-testid="dash-assignee"><User className="w-3 h-3 mr-1 text-slate-400" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All assignees</SelectItem>
            {distinctAssignees.map((u) => (
              <SelectItem key={u.id} value={u.id}>{u.name}{u.role ? ` · ${u.role.toUpperCase()}` : ''}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="h-9 text-xs bg-white" data-testid="dash-role"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="csm">CSM</SelectItem>
            <SelectItem value="credit">Credit Team</SelectItem>
            <SelectItem value="broker">Broker</SelectItem>
          </SelectContent>
        </Select>
        <Select value={phase} onValueChange={setPhase}>
          <SelectTrigger className="h-9 text-xs bg-white" data-testid="dash-phase"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All phases</SelectItem>
            <SelectItem value="pre_lodgement">Pre-Lodgement</SelectItem>
            <SelectItem value="post_lodgement">Post-Lodgement</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger className="h-9 text-xs bg-white" data-testid="dash-priority"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            <SelectItem value="P1">P1 critical</SelectItem>
            <SelectItem value="P2">P2</SelectItem>
            <SelectItem value="P3">P3</SelectItem>
          </SelectContent>
        </Select>
        <button
          onClick={() => setOverdueOnly((v) => !v)}
          className={`h-9 rounded-md text-[11px] font-black uppercase tracking-widest transition-colors ${overdueOnly ? 'bg-rose-600 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100'}`}
          data-testid="dash-overdue"
        >
          <Flame className="w-3 h-3 inline mr-1" /> Overdue
        </button>
      </div>

      {/* Body — Kanban or list, user toggle */}
      {loading ? (
        <p className="text-sm text-slate-500 py-10 text-center">Loading your tasks…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center">
          <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-600 mb-2" />
          <p className="text-sm font-black text-emerald-900">Inbox zero</p>
          <p className="text-[12px] text-emerald-700 mt-0.5">Nothing needs attention with your current filters. Great work.</p>
        </div>
      ) : view === 'kanban' ? (
        <div className="flex gap-3 overflow-x-auto pb-4 -mx-1 px-1" data-testid="dash-kanban">
          <Column title="Overdue"                icon={AlertTriangle} tone="rose"   items={groups.overdue}  onOpen={onOpenPack} />
          <Column title="Due today"              icon={Clock}         tone="amber"  items={groups.dueToday} onOpen={onOpenPack} />
          <Column title="Waiting on someone else" icon={Timer}        tone="purple" items={groups.waiting}  onOpen={onOpenPack} />
          <Column title="Upcoming"               icon={Inbox}         tone="slate"  items={groups.upcoming} onOpen={onOpenPack} />
        </div>
      ) : (
        <div className="space-y-5" data-testid="dash-list">
          <ListGroup title="Overdue"                icon={AlertTriangle} tone="rose"   items={groups.overdue}  onOpen={onOpenPack} />
          <ListGroup title="Due today"              icon={Clock}         tone="amber"  items={groups.dueToday} onOpen={onOpenPack} />
          <ListGroup title="Waiting on someone else" icon={Timer}        tone="purple" items={groups.waiting}  onOpen={onOpenPack} />
          <ListGroup title="Upcoming"               icon={Inbox}         tone="slate"  items={groups.upcoming} onOpen={onOpenPack} />
        </div>
      )}
    </div>
  );
}

const TONE_META = {
  rose:   { bg: '#fee2e2', ink: '#7f1d1d', accent: '#dc2626', pale: '#fff1f2', border: '#fecaca' },
  amber:  { bg: '#fef3c7', ink: '#78350f', accent: '#d97706', pale: '#fffbeb', border: '#fcd34d' },
  purple: { bg: '#f3e8ff', ink: '#581c87', accent: '#9333ea', pale: '#faf5ff', border: '#d8b4fe' },
  slate:  { bg: '#f1f5f9', ink: '#0f172a', accent: '#64748b', pale: '#f8fafc', border: '#e2e8f0' },
};

function Column({ title, icon: Icon, tone, items, onOpen }) {
  const t = TONE_META[tone] || TONE_META.slate;
  return (
    <div className="w-[320px] flex-shrink-0">
      {/* Column header */}
      <div
        className="rounded-t-xl border border-b-0 px-3 py-2 flex items-center gap-2"
        style={{ background: t.pale, borderColor: t.border }}
      >
        <span className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ background: t.accent }}>
          <Icon className="w-3.5 h-3.5 text-white" />
        </span>
        <h3 className="text-[12px] font-black uppercase tracking-widest" style={{ color: t.ink }}>{title}</h3>
        <span className="ml-auto text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: t.bg, color: t.ink }}>{items.length}</span>
      </div>
      {/* Column body */}
      <div
        className="rounded-b-xl border p-2 space-y-2 min-h-[120px] max-h-[calc(100vh-320px)] overflow-y-auto"
        style={{ background: '#ffffff', borderColor: t.border }}
      >
        {items.length === 0 ? (
          <div className="text-center py-6 text-[11px] italic text-slate-400">Nothing here</div>
        ) : items.map((task) => (
          <TaskCard key={task.key} task={task} onOpen={() => onOpen && onOpen(task.packId)} />
        ))}
      </div>
    </div>
  );
}

// ─── List view — grouped rows with a table, matching the Ideas Board pattern.
function ListGroup({ title, icon: Icon, tone, items, onOpen }) {
  if (!items || items.length === 0) return null;
  const t = TONE_META[tone] || TONE_META.slate;
  return (
    <div className="rounded-xl border overflow-hidden shadow-sm" style={{ borderColor: t.border }}>
      <div className="px-3 py-2 flex items-center gap-2 border-b" style={{ background: t.pale, borderColor: t.border }}>
        <span className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ background: t.accent }}>
          <Icon className="w-3.5 h-3.5 text-white" />
        </span>
        <h3 className="text-[12px] font-black uppercase tracking-widest" style={{ color: t.ink }}>{title}</h3>
        <span className="text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: t.bg, color: t.ink }}>{items.length}</span>
      </div>
      <div className="overflow-x-auto bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/60 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left w-14">Pri</th>
              <th className="px-3 py-2 text-left">Client / Action</th>
              <th className="px-3 py-2 text-left">Phase</th>
              <th className="px-3 py-2 text-left">Stage</th>
              <th className="px-3 py-2 text-left">Assignee</th>
              <th className="px-3 py-2 text-left">Waiting on</th>
              <th className="px-3 py-2 text-left">Due</th>
              <th className="px-3 py-2 text-left">Age</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((task) => (
              <tr
                key={task.key}
                className="hover:bg-slate-50 cursor-pointer"
                style={{ background: task.isOverdue ? '#fff1f2' : undefined }}
                onClick={() => onOpen && onOpen(task.packId)}
                data-testid={`dash-list-row-${task.key}`}
              >
                <td className="px-3 py-2">
                  <span
                    className="inline-flex items-center justify-center w-7 h-7 rounded-full text-[10px] font-black text-white"
                    style={{ background: task.priority === 'P1' ? '#dc2626' : task.priority === 'P2' ? '#d97706' : '#64748b' }}
                  >{task.priority}</span>
                </td>
                <td className="px-3 py-2">
                  <p className="font-black text-slate-900 text-[12.5px] leading-tight">{task.client}</p>
                  <p className="text-[11.5px] text-slate-700 leading-snug">{task.title}</p>
                  {task.lender && <p className="text-[10.5px] text-slate-400">{task.lender}</p>}
                </td>
                <td className="px-3 py-2">
                  <span className="text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                    {task.phase === 'pre_lodgement' ? 'Pre-Lodgement' : 'Post-Lodgement'}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ background: task.stageBg, color: task.stageFg }}>
                    <Zap className="w-2.5 h-2.5" /> {task.stageLabel}
                  </span>
                </td>
                <td className="px-3 py-2">
                  {task.assigneeName ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-800">
                      <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black text-white bg-indigo-600">{initials(task.assigneeName)}</span>
                      {task.assigneeName}
                    </span>
                  ) : <span className="text-[11px] italic text-slate-400">Unassigned</span>}
                </td>
                <td className="px-3 py-2">
                  {task.waitingLabel ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: task.waitingBg, color: task.waitingFg }}>
                      <Clock className="w-2.5 h-2.5" /> {task.waitingLabel}
                    </span>
                  ) : <span className="text-[11px] text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2">
                  {task.dueDate ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-black tabular-nums px-1.5 py-0.5 rounded" style={{
                      background: task.isOverdue ? '#fee2e2' : task.isDueToday ? '#fef3c7' : '#f1f5f9',
                      color: task.isOverdue ? '#991b1b' : task.isDueToday ? '#92400e' : '#334155',
                    }}>
                      <Timer className="w-2.5 h-2.5" />
                      {task.isOverdue ? `${Math.abs(task.dueIn)}d over` : task.isDueToday ? 'Due today' : fmtDate(task.dueDate)}
                    </span>
                  ) : <span className="text-[11px] text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2 text-[11px] text-slate-500 tabular-nums">Open {task.ageDays}d</td>
                <td className="px-3 py-2 text-right">
                  <ChevronRight className="w-4 h-4 text-slate-300 inline" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TaskCard({ task, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left rounded-xl border bg-white hover:shadow-md transition-all p-3 flex items-start gap-3"
      style={{
        borderColor: task.isOverdue ? '#fecaca' : '#e2e8f0',
        background: task.isOverdue ? 'linear-gradient(0deg, #fff1f2 0%, #ffffff 60%)' : '#ffffff',
      }}
      data-testid={`dash-task-${task.key}`}
    >
      <div className="shrink-0 flex flex-col items-center gap-1">
        <span
          className="w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-black text-white"
          style={{ background: task.priority === 'P1' ? '#dc2626' : task.priority === 'P2' ? '#d97706' : '#64748b' }}
        >{task.priority}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className="text-[10.5px] font-black uppercase tracking-widest text-slate-500">{task.phase === 'pre_lodgement' ? 'Pre-Lodgement' : 'Post-Lodgement'}</span>
          <span className="text-[11.5px] font-black text-slate-900">{task.client}</span>
          {task.lender && <span className="text-[10.5px] font-semibold text-slate-500">· {task.lender}</span>}
        </div>
        <p className="text-[13px] font-black text-slate-900 leading-tight mb-1">{task.title}</p>
        {task.docsRequired && task.docsRequired !== task.title && (
          <p className="text-[11px] text-slate-600 mb-1.5">Docs: {task.docsRequired}</p>
        )}
        <div className="flex items-center gap-1.5 flex-wrap">
          {task.assigneeName && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: '#f1f5f9', color: '#334155' }}>
              <span className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-black text-white" style={{ background: '#4f46e5' }}>{initials(task.assigneeName)}</span>
              {task.assigneeName}
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: task.stageBg, color: task.stageFg }}>
            <Zap className="w-2.5 h-2.5" /> {task.stageLabel}
          </span>
          {task.waitingLabel && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: task.waitingBg, color: task.waitingFg }}>
              <Clock className="w-2.5 h-2.5" /> Waiting on {task.waitingLabel}
            </span>
          )}
          {task.dueDate && (
            <span className="inline-flex items-center gap-1 text-[10px] font-black tabular-nums px-1.5 py-0.5 rounded" style={{
              background: task.isOverdue ? '#fee2e2' : task.isDueToday ? '#fef3c7' : '#f1f5f9',
              color: task.isOverdue ? '#991b1b' : task.isDueToday ? '#92400e' : '#334155',
            }}>
              <Timer className="w-2.5 h-2.5" />
              {task.isOverdue ? `${Math.abs(task.dueIn)}d over` : task.isDueToday ? 'Due today' : `Due ${fmtDate(task.dueDate)}`}
            </span>
          )}
          <span className="text-[10px] font-semibold text-slate-500">Open {task.ageDays}d</span>
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-300 shrink-0 mt-1.5" />
    </button>
  );
}
